import React, { memo, MouseEvent, useContext } from "react";
import styled, { css, useTheme } from "styled-components";
import { ChevronDown } from "lucide-react";
import {
  Direction,
  LanguageNameSpaces,
  Signal,
  SignalDataWithKey,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  ICON_TYPES,
  SIGNAL_EXTRA_DETAILS_TABS,
} from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";
import {
  CardsSelectorContext,
  ContextProps,
} from "@shared/common/CardsList/CardsSelector.context";
import { CheckboxData } from "@shared/common/CardsList/types";
import IconWithFallback from "Global/IconWithFallback";
import {
  directionColor,
  getNewLookTokens,
  getPanelShadow,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
} from "common/NewLook/tokens.ts";
import { formatFilledPercent } from "common/NewLook/priceLadder.ts";
import { ladderToneColor } from "common/NewLook/PriceLadder.tsx";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import LongPress from "common/Gestures/LongPress.tsx";
import Checkbox from "common/Checkbox/Checkbox";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import {
  formatTimeInTrade,
  useLadderPriceFormat,
} from "features/trades/NewLook/useTradeLadder.ts";
import { formatSignedPercent } from "features/trades/NewLook/useMoneyFormat.ts";
import {
  PhoneCardSlot,
  PhoneSkeleton,
} from "features/dashboard/PhoneDashboard/PhoneParts";
import { SignalFollowAlertIcon } from "features/signals/SignalFollow/SignalFollowAlertIcon";
import SignalCardActions from "features/signals/SignalsCards/SignalCardActions";
import PhoneSignalDetails from "features/signals/NewLook/PhoneSignalDetails.tsx";
import {
  getSignalFollowState,
  getSignalPositionTerms,
  getSignalPotentialProfit,
  getSignalProfit,
  getSignalProgress,
  getSignalRiskReward,
  isSignalWaitingForEntry,
} from "features/signals/NewLook/phoneSignalCardFigures.ts";
import { getSignalExtraDetailsRoute } from "utils/routes.tsx";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import {
  SIGNAL_CARD_ACTIONS_ID,
  SIGNAL_CARD_ENTRIES_ID,
  SIGNAL_CARD_EXPAND_ID,
  SIGNAL_CARD_FIGURE_ID,
  SIGNAL_CARD_FOLLOW_ID,
  SIGNAL_CARD_FOOTER_ID,
  SIGNAL_CARD_POSITION_ID,
  SIGNAL_CARD_PRICE_ID,
  SIGNAL_CARD_PROGRESS_ID,
  SIGNAL_CARD_ROW_ID,
  SIGNAL_CARD_SELECT_ID,
  SIGNAL_CARD_SYMBOL_ID,
  SIGNAL_CARD_TAKE_PROFITS_ID,
} from "testIds";
import { translateOptionName } from "@shared/common/i18n/optionNames";

const DETAILS_GAP = { paddingTop: 12 };
const FIGURE_MIN_FONT_SIZE = 9;
const TEXT_MIN_FONT_SIZE = 10;
const SYMBOL_MIN_FONT_SIZE = 12;
const FOOTER_SEPARATOR = " · ";

interface PhoneSignalCardProps {
  signal: SignalDataWithKey;
  expanded: boolean;
  onToggleExpanded: (exchangeSignalId: number) => void;
}

interface CardState {
  checkbox?: CheckboxData;
  onToggle: () => void;
  onOpenDetails?: () => void;
}

const PhoneSignalCard = (props: PhoneSignalCardProps) => (
  <PhoneCardSlot>
    <CardBody {...props} />
  </PhoneCardSlot>
);

export default memo(PhoneSignalCard);

const CardBody = (props: PhoneSignalCardProps) => {
  const state = useCardState(props);
  const card = (
    <Card
      data-testid={SIGNAL_CARD_ROW_ID}
      data-signal-id={props.signal.signal_id}
      data-status={props.signal.is_open ? "open" : "closed"}
      $selected={!!state.checkbox?.isSelected}
      onClick={state.onToggle}
    >
      <CardHead signal={props.signal} checkbox={state.checkbox} />
      <ProgressBlock signal={props.signal} />
      <CardFooter
        signal={props.signal}
        expanded={props.expanded}
        onToggle={state.onToggle}
        isEditMode={!!state.checkbox}
      />
      <ExpandCollapse
        expanded={props.expanded}
        animateAuto={false}
        style={DETAILS_GAP}
      >
        <PhoneSignalDetails
          signal={props.signal}
          onOpenDetails={state.onOpenDetails}
        />
      </ExpandCollapse>
    </Card>
  );
  if (!state.onOpenDetails) return card;
  return (
    <LongPress
      triggerElement={card}
      onLongPress={state.onOpenDetails}
      ignoreControls={true}
    />
  );
};

const CardHead = ({
  signal,
  checkbox,
}: {
  signal: Signal;
  checkbox?: CheckboxData;
}) => (
  <Head>
    {checkbox && <SelectBox checkbox={checkbox} />}
    <CoinIcon signal={signal} />
    <Names>
      <SymbolName data-testid={SIGNAL_CARD_SYMBOL_ID} title={signal.symbol}>
        <AutoSizeText inheritFont={true} minFontSizePx={SYMBOL_MIN_FONT_SIZE}>
          {signal.symbol}
        </AutoSizeText>
      </SymbolName>
      <PositionLine signal={signal} />
    </Names>
    <HeadFigures signal={signal} />
  </Head>
);

const SelectBox = ({ checkbox }: { checkbox: CheckboxData }) => (
  <CheckboxHit
    data-testid={SIGNAL_CARD_SELECT_ID}
    onClick={(event) => stopAnd(event, checkbox.onClick)}
  >
    <Checkbox checked={checkbox.isSelected} $size={18} />
  </CheckboxHit>
);

const CoinIcon = ({ signal }: { signal: Signal }) => {
  const followState = getSignalFollowState(signal);
  return (
    <IconSlot>
      <IconWithFallback
        coinOrExchangeName={signal.symbol}
        iconType={ICON_TYPES.COIN}
        pxIconSize={32}
      />
      {followState && (
        <FollowBadge
          data-testid={SIGNAL_CARD_FOLLOW_ID}
          data-state={followState}
          onClick={(event) => event.stopPropagation()}
        >
          <SignalFollowAlertIcon signal={signal} />
        </FollowBadge>
      )}
    </IconSlot>
  );
};

const PositionLine = ({ signal }: { signal: Signal }) => {
  const tokens = getNewLookTokens(useTheme());
  const side = signal.signal_extra_data?.["Long/Short"];
  const terms = getSignalPositionTerms(signal, translateOptionName);
  return (
    <Meta data-testid={SIGNAL_CARD_POSITION_ID}>
      <AutoSizeText inheritFont={true} minFontSizePx={TEXT_MIN_FONT_SIZE}>
        {side && (
          <Side
            style={{
              color: side === Direction.SHORT ? tokens.down : tokens.up,
            }}
          >
            {translateOptionName(side)}
          </Side>
        )}
        {terms && `${side ? " " : ""}${terms}`}
      </AutoSizeText>
    </Meta>
  );
};

const HeadFigures = ({ signal }: { signal: Signal }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const tokens = getNewLookTokens(useTheme());
  const profit = getSignalProfit(signal);
  return (
    <Figures>
      {isSignalWaitingForEntry(signal) ? (
        <Waiting data-testid={SIGNAL_CARD_FIGURE_ID}>
          {t("phone.card_waiting")}
        </Waiting>
      ) : (
        <Profit
          data-testid={SIGNAL_CARD_FIGURE_ID}
          style={{ color: directionColor(tokens, profit) }}
        >
          {formatSignedPercent(profit)}
        </Profit>
      )}
      {signal.is_open && <LivePrice signal={signal} />}
    </Figures>
  );
};

const LivePrice = ({ signal }: { signal: Signal }) => {
  const formatPrice = useLadderPriceFormat(signal.symbol, signal.exchange);
  const price = Number(signal.price);
  if (!isFinite(price) || price <= 0) return null;
  return (
    <Price data-testid={SIGNAL_CARD_PRICE_ID}>
      <AutoSizeText inheritFont={true} minFontSizePx={FIGURE_MIN_FONT_SIZE}>
        {formatPrice(price)}
      </AutoSizeText>
    </Price>
  );
};

const ProgressBlock = ({ signal }: { signal: Signal }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const theme = useTheme();
  const progress = getSignalProgress(signal);
  return (
    <Progress
      data-testid={SIGNAL_CARD_PROGRESS_ID}
      data-entries={progress.entries}
      data-take-profits={progress.takeProfits}
    >
      <Bar>
        <Segment>
          <Fill
            style={{
              width: `${progress.entries * 100}%`,
              background: ladderToneColor(theme, "entry"),
            }}
          />
        </Segment>
        <Segment>
          <Fill
            style={{
              width: `${progress.takeProfits * 100}%`,
              background: ladderToneColor(theme, "take_profit"),
            }}
          />
        </Segment>
      </Bar>
      <Ends>
        <End data-testid={SIGNAL_CARD_ENTRIES_ID} $isEnd={false}>
          <EndName>{t("phone.card_entries")}</EndName>{" "}
          {formatFilledPercent(progress.entries)}
        </End>
        <End data-testid={SIGNAL_CARD_TAKE_PROFITS_ID} $isEnd={true}>
          <EndName>{t("phone.card_take_profits")}</EndName>{" "}
          {formatFilledPercent(progress.takeProfits)}
        </End>
      </Ends>
    </Progress>
  );
};

const CardFooter = ({
  signal,
  expanded,
  onToggle,
  isEditMode,
}: {
  signal: Signal;
  expanded: boolean;
  onToggle: () => void;
  isEditMode: boolean;
}) => {
  const age = formatTimeInTrade(signal.graph_data?.time_passed);
  return (
    <Footer>
      <FooterPlan signal={signal} />
      <Controls>
        {age && <Age>{age}</Age>}
        <ExpandButton expanded={expanded} onToggle={onToggle} />
        <Actions data-testid={SIGNAL_CARD_ACTIONS_ID}>
          {!isEditMode && <SignalCardActions signal={signal} />}
        </Actions>
      </Controls>
    </Footer>
  );
};

const FooterPlan = ({ signal }: { signal: Signal }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const potential = getSignalPotentialProfit(signal);
  const riskReward = getSignalRiskReward(signal);
  return (
    <FooterMeta data-testid={SIGNAL_CARD_FOOTER_ID}>
      <AutoSizeText inheritFont={true} minFontSizePx={TEXT_MIN_FONT_SIZE}>
        {potential !== undefined && (
          <>
            {t("phone.card_potential")}{" "}
            <FooterValue>{formatSignedPercent(potential)}</FooterValue>
          </>
        )}
        {potential !== undefined && riskReward && FOOTER_SEPARATOR}
        {riskReward && (
          <>
            {t("phone.card_risk_reward")}{" "}
            <FooterValue>{riskReward}</FooterValue>
          </>
        )}
      </AutoSizeText>
    </FooterMeta>
  );
};

const ExpandButton = ({
  expanded,
  onToggle,
}: {
  expanded: boolean;
  onToggle: () => void;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.SIGNALS);
  const label = t(
    expanded ? "phone.card_hide_details" : "phone.card_show_details"
  );
  return (
    <ControlButton
      type="button"
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      onClick={(event) => stopAnd(event, onToggle)}
      data-testid={SIGNAL_CARD_EXPAND_ID}
    >
      <Chevron $expanded={expanded} size={18} strokeWidth={2} />
    </ControlButton>
  );
};

export const PhoneSignalCardSkeleton = () => (
  <PhoneCardSlot aria-hidden={true}>
    <Card>
      <Head>
        <PhoneSkeleton $width={32} $height={32} $radius={16} />
        <Names>
          <PhoneSkeleton $width={112} $height={13} />
          <PhoneSkeleton $width={90} $height={10} style={SKELETON_META_STYLE} />
        </Names>
        <PhoneSkeleton $width={56} $height={14} />
      </Head>
      <Progress>
        <PhoneSkeleton $width={400} $height={4} $radius={2} />
        <Ends>
          <PhoneSkeleton $width={72} $height={11} />
          <PhoneSkeleton $width={56} $height={11} />
        </Ends>
      </Progress>
      <Footer>
        <PhoneSkeleton $width={150} $height={10} />
      </Footer>
    </Card>
  </PhoneCardSlot>
);

const useCardState = ({
  signal,
  onToggleExpanded,
}: PhoneSignalCardProps): CardState => {
  const { historyPush } = useHistoryWithQuery();
  const displayTradeDetails = useDisplayTradeDetails();
  const { getCheckboxProps, isEditMode } =
    useContext<ContextProps<SignalDataWithKey> | undefined>(
      CardsSelectorContext
    ) || {};
  return {
    checkbox: isEditMode ? getCheckboxProps?.(signal) : undefined,
    onToggle: () => onToggleExpanded(signal.exchange_signal_id),
    onOpenDetails: displayTradeDetails
      ? () =>
          historyPush(
            getSignalExtraDetailsRoute(
              location.pathname,
              signal.signal_id,
              SIGNAL_EXTRA_DETAILS_TABS.GENERAL,
              false
            )
          )
      : undefined,
  };
};

const stopAnd = (event: MouseEvent, action: () => void) => {
  event.stopPropagation();
  action();
};

const SKELETON_META_STYLE = { marginTop: 7 };

const Card = styled.div<{ $selected?: boolean }>`
  padding: 14px 16px 12px;
  user-select: none;
  -webkit-touch-callout: none;
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  ${(p) =>
    p.$selected &&
    css`
      box-shadow: inset 0 0 0 1.5px ${p.theme.colors.primaryBlue};
    `}
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 150ms;
  &:active {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
`;
const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
const CheckboxHit = styled.div`
  flex: none;
  display: flex;
  margin: -6px -2px -6px -6px;
  padding: 6px;
`;
const IconSlot = styled.div`
  position: relative;
  flex: none;
  display: flex;
  margin-right: 2px;
`;
const FollowBadge = styled.span`
  position: absolute;
  right: -5px;
  bottom: -5px;
  display: flex;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: 0 0 0 1.5px ${(p) => getNewLookTokens(p.theme).panel};
  .anticon {
    margin-left: 0 !important;
    font-size: 15px !important;
  }
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
`;
const SymbolName = styled.div`
  min-width: 0;
  ${newLookPhoneType.cardTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Meta = styled.div`
  min-width: 0;
  margin-top: 2px;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Side = styled.span`
  font-weight: 500;
`;
const Figures = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 45%;
`;
const Profit = styled.span`
  ${newLookPhoneType.cardTitle};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Waiting = styled.span`
  ${newLookPhoneType.label};
  font-weight: 500;
  line-height: 20px;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  white-space: nowrap;
`;
const Price = styled.span`
  max-width: 100%;
  margin-top: 2px;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Progress = styled.div`
  margin-top: 14px;
`;
const Bar = styled.div`
  display: flex;
  gap: 4px;
  height: 4px;
`;
const Segment = styled.div`
  position: relative;
  flex: 1;
  overflow: hidden;
  border-radius: ${(p) => p.theme.radii.pill}px;
  background: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.18)};
`;
const Fill = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  border-radius: inherit;
`;
const Ends = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
`;
const End = styled.div<{ $isEnd: boolean }>`
  flex: 0 1 auto;
  min-width: 0;
  ${newLookPhoneType.meta};
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: ${(p) => (p.$isEnd ? "right" : "left")};
  white-space: nowrap;
`;
const EndName = styled.span`
  font-weight: 400;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 20px;
  margin-top: 10px;
`;
const FooterMeta = styled.div`
  flex: 1;
  min-width: 0;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const FooterValue = styled.span`
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Controls = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
  margin: -8px -8px -8px 0;
`;
const Age = styled.span`
  margin-right: 4px;
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;
const Chevron = styled(ChevronDown)<{ $expanded: boolean }>`
  transition: transform 0.2s;
  transform: rotate(${(p) => (p.$expanded ? 180 : 0)}deg);
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  &:empty {
    display: none;
  }
`;
